import type {HouseRuleDisplay, HouseRuleType, VacasaUnitResponse} from "@/entities/rental/api";

export interface PropertyRule {
    type: HouseRuleType;
    title: string;
    description?: string;
    allowed: boolean;
}

class PropertyRulesClass {
    private readonly unit: VacasaUnitResponse;

    constructor(unit: VacasaUnitResponse) {
        this.unit = unit;
    }

    // ==================== Contract Terms ====================

    get contractTerms(): string {
        return this.unit.contractTerms || "";
    }

    // ==================== Rules ====================

    /**
     * Map HouseRuleDisplay from API to PropertyRule
     */
    private mapHouseRuleToPropertyRule(rule: HouseRuleDisplay): PropertyRule {
        return {
            type: rule.type,
            title: rule.title,
            description: rule.description,
            allowed: rule.allowed,
        };
    }

    /**
     * Get icon name for a rule type (kebab-case)
     * Pet → pet, AgeRestriction → age-restriction, etc.
     */
    getIconName(type: HouseRuleType): string {
        return type.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
    }

    /** Rule types that have separate "allowed" and "not allowed" icons */
    private static readonly RULES_WITH_ALLOWED_VARIANT: Array<HouseRuleType> = ["Pet", "Smoking", "Children", "Event"];

    /**
     * Get icon name for a rule, using allowed/not-allowed variant when applicable.
     * Pet, Smoking, Children → "pet-allowed" | "pet-not-allowed", etc.
     * Other types → same as getIconName(type).
     */
    getIconNameForRule(rule: PropertyRule): string {
        const base = this.getIconName(rule.type);
        if (PropertyRulesClass.RULES_WITH_ALLOWED_VARIANT.includes(rule.type)) {
            return rule.allowed ? `${base}-allowed` : `${base}-not-allowed`;
        }
        return base;
    }

    /**
     * Get rules from houseRulesDisplay API field
     */
    get rules(): Array<PropertyRule> {
        return (this.unit.houseRulesDisplay ?? []).map((rule) => this.mapHouseRuleToPropertyRule(rule));
    }
}

export type PropertyRulesInstance = PropertyRulesClass;

export function PropertyRules(unit: VacasaUnitResponse): PropertyRulesInstance {
    return new PropertyRulesClass(unit);
}
